import {
  type KeyboardEvent, type MutableRefObject, type ReactNode
} from "react";

interface OtpEntryProps {
  digits: string[];
  refs: MutableRefObject<(HTMLInputElement | null)[]>;
  onChange: (index: number, value: string) => void;
  onKeyDown: (index: number, e: KeyboardEvent) => void;
  length: number;
  timeLeft: number;
  formattedTime: string;
  /** Channel-specific "we sent a code to X" blurb. */
  infoMessage: ReactNode;
  /** Non-error banner shown under the blurb (e.g. the resend cooldown). */
  notice?: ReactNode;
  /** Invoked from the "Try again" link once the code has expired. */
  onRegenerate: () => void;
}

/**
 * The "enter the six-character code" step shared by the SMS and iMessage pairing
 * modals: the info banner, the OTP input grid (wired to ``useOtpInput``), and
 * the expiry countdown / expired-with-retry states.
 */
export function OtpEntry({
  digits,
  refs,
  onChange,
  onKeyDown,
  length,
  timeLeft,
  formattedTime,
  infoMessage,
  notice,
  onRegenerate,
}: OtpEntryProps) {
  return (
    <div className="mt-4">
      <div className="mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-sm text-blue-800">
            {infoMessage}
          </p>
        </div>
        {notice}
      </div>

      {timeLeft > 0 ? (
        <>
          <div className="flex justify-center gap-2 mb-4">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  refs.current[index] = el;
                }}
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="one-time-code"
                maxLength={index === 0 ? length : 1}
                value={digit}
                onChange={(e) => onChange(index, e.target.value)}
                onKeyDown={(e) => onKeyDown(index, e)}
                className="w-12 h-14 text-center text-xl font-mono border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              Code expires in:{" "}
              <strong>{formattedTime}</strong>
            </p>
          </div>
        </>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">
            Verification code has expired.
          </p>
          <button
            onClick={onRegenerate}
            className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
